import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import IconButton from '@mui/material/IconButton';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import { useQueryParam, StringParam } from 'use-query-params';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown.js';
import { ClickCitationContext } from '@trinity/chat-components/components/Chat/context.js';
import type { TranscriptAPI } from '@trinity/chat';
import { useModal } from '@trinity/chat-components/hooks/useModal.js';
import { TranscriptIdContext } from '@/containers/Transcript/context';
import { useTranscriptExtractedData } from '@/utils/api';
import { Skeleton } from '@/components/Transcript';
import { ExtractedDataJsonModal } from './Modal.ExtractedDataJson';
import { SourceCitationModal } from './Modal.SourceCitation';
import type { SourceInfo } from './Modal.SourceCitation';
import styles from '@/screens/Transcript/style/Tab.Summary.module.css';

export const ExtractedData = () => {
  const transcriptId = useContext(TranscriptIdContext);
  const [sectionParam] = useQueryParam('section', StringParam);
  const scrolledRef = useRef(false);

  const query = useTranscriptExtractedData(
    { transcriptId },
    { enabled: !!transcriptId, refetchOnWindowFocus: false, refetchOnReconnect: false, retry: 1 }
  );

  const isLoading = query.isLoading;
  const items = query.data?.items ?? [];
  const hasData = !isLoading && items.length > 0;
  const hasNoData = !isLoading && items.length === 0;

  useEffect(() => {
    if (!sectionParam || !hasData || scrolledRef.current) return;
    const el = document.getElementById(`extracted-data-${sectionParam}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      scrolledRef.current = true;
    }
  }, [sectionParam, hasData]);

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <div className={styles.header}>
          <div className={styles.title}>Extracted Data</div>
        </div>
        <div className={styles.main}>
          {isLoading && <Skeleton.Summary rows={3} />}
          {hasData && items.map((item) => <ExtractedDataSection key={item.key} item={item} />)}
          {hasNoData && <Placeholder />}
        </div>
      </div>
    </div>
  );
};

ExtractedData.displayName = 'Transcript.Tab.ExtractedData';

type ExtractedDataItem = TranscriptAPI.GetTranscriptExtractedData.ExtractedDataItem;

const ExtractedDataSection = ({ item }: { item: ExtractedDataItem }) => {
  const [open, setOpen] = useState(true);
  const [toggleModal, JsonModal] = useModal(ExtractedDataJsonModal);
  const [selectedSource, setSelectedSource] = useState<SourceInfo | null>(null);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);

  const labelToSource = useMemo(() => {
    if (!item.sources || typeof item.sources !== 'object') return new Map<string, SourceInfo>();
    const sources = item.sources as Record<string, SourceInfo & { citationLabel?: string }>;
    const map = new Map<string, SourceInfo>();
    for (const [sourceId, source] of Object.entries(sources)) {
      if (source.citationLabel) {
        map.set(source.citationLabel, { ...source, sourceId });
      }
    }
    return map;
  }, [item.sources]);

  const handleCitationClick = useCallback(
    (citationValue: string) => {
      const source = labelToSource.get(citationValue);
      if (source) {
        setSelectedSource(source);
        setSourceModalOpen(true);
      }
    },
    [labelToSource]
  );

  return (
    <SectionRoot id={`extracted-data-${item.key}`}>
      <SectionHeader onClick={() => setOpen((o) => !o)}>
        <SectionTitle>{item.title}</SectionTitle>
        <SectionActions>
          {item.extractedData && (
            <Tooltip title="View extracted JSON">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleModal();
                }}
              >
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <ChevronButton size="small" $open={open}>
            <ExpandMoreIcon fontSize="small" />
          </ChevronButton>
        </SectionActions>
      </SectionHeader>
      {open && (
        <>
          {item.markdownSummary && (
            <MarkdownContent>
              <ClickCitationContext.Provider value={handleCitationClick}>
                <ChatMarkdown value={item.markdownSummary} />
              </ClickCitationContext.Provider>
            </MarkdownContent>
          )}
          {!item.markdownSummary && <NoSummary>No summary available</NoSummary>}
        </>
      )}
      <JsonModal title={item.title} extractedData={item.extractedData} />
      <SourceCitationModal
        open={sourceModalOpen}
        onClose={() => setSourceModalOpen(false)}
        source={selectedSource}
      />
    </SectionRoot>
  );
};

const Placeholder = memo(() => (
  <div className={styles.empty}>
    <div className={styles.placeholder}>No summary available for this transcript.</div>
  </div>
));

const SectionRoot = styled.div`
  margin-bottom: 24px;
  scroll-margin-top: 170px;
`;

const SectionHeader = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${theme.palette.gray.light1};
  cursor: pointer;
  user-select: none;
  color: ${theme.palette.black.main};
`
);

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-family: var(--font-semibold);
  color: var(--black-l);
`;

const ChevronButton = styled(IconButton)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(0deg)' : 'rotate(-90deg)')};
`;

const MarkdownContent = styled.div`
  color: var(--black-l);
  line-height: 1.6;
  padding: 4px 0 0 16px;
`;

const NoSummary = styled.div`
  color: var(--gray);
  font-style: italic;
  padding: 4px 0 0 16px;
`;
